import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Calendar,
  ChevronRight,
  CloudDownload,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { StatusBadge } from "@/components/StatusBadge";
import { useTournaments } from "@/hooks/useTournaments";
import type { TournamentStatus } from "@/lib/api";
import { formatItalianDateShort, daysBetween } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_FILTERS: { value: TournamentStatus | "ALL"; label: string }[] = [
  { value: "ALL", label: "Tutti" },
  { value: "DRAFT", label: "Bozze" },
  { value: "TEXTS_GENERATED", label: "Testi ok" },
  { value: "FLYER_GENERATED", label: "Locandina ok" },
  { value: "EMAILS_SENT", label: "Email inviate" },
  { value: "SOCIAL_RUNNING", label: "Social attivo" },
  { value: "COMPLETED", label: "Completati" },
];

export default function TournamentList() {
  const [statusFilter, setStatusFilter] = useState<TournamentStatus | "ALL">("ALL");
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useTournaments({
    status: statusFilter === "ALL" ? undefined : statusFilter,
    page_size: 200,
  });

  const filtered = (data?.items ?? []).filter((t) =>
    search.trim()
      ? t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.slug.toLowerCase().includes(search.toLowerCase())
      : true,
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tornei</h1>
          <p className="text-sm text-slate-500">
            {data ? `${data.pagination.total} totali` : "…"}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/tournaments/import-playtomic">
            <Button variant="outline">
              <CloudDownload className="h-4 w-4" />
              <span className="hidden sm:inline">Importa da Playtomic</span>
            </Button>
          </Link>
          <Link to="/tournaments/new">
            <Button variant="primary">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuovo torneo</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o slug…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                statusFilter === f.value
                  ? "bg-moving-500 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <Card>
        <CardBody className="p-0">
          {error ? (
            <div className="p-5 text-sm text-red-700">
              Errore caricamento: {String(error)}
            </div>
          ) : isLoading ? (
            <div className="p-5 text-sm text-slate-500">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-5 py-10 text-center text-sm text-slate-500">
              Nessun torneo trovato.
              {search || statusFilter !== "ALL" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("ALL");
                  }}
                >
                  Azzera filtri
                </Button>
              ) : null}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((t) => {
                const days = daysBetween(new Date(), t.date);
                return (
                  <li key={t.slug}>
                    <Link
                      to={`/tournaments/${t.slug}`}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50 md:px-5 md:py-4"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-moving-50 text-moving-700">
                        <Calendar className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-medium text-slate-900">
                            {t.name}
                          </span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                          <span>{formatItalianDateShort(t.date)}</span>
                          <span>·</span>
                          <span>
                            {days > 0
                              ? `tra ${days}g`
                              : days === 0
                                ? "oggi"
                                : `${-days}g fa`}
                          </span>
                          <span>·</span>
                          <span className="font-mono text-slate-400">{t.slug}</span>
                        </div>
                        {t.next_action ? (
                          <div className="mt-1 text-xs text-court-600">
                            → {t.next_action}
                          </div>
                        ) : null}
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
